import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import {
  Paper,
  Stack,
  Divider,
  SizedBox,
  Typography,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import messages from './messages';
import ResidentHistoryTable from './ResidentHistoryTable';

type UnitHistoryRowData = {
  id: string,
  floorPlanId?: string,
  floorPlan: string,
  setAside?: string | null,
  startDate: string | null,
  endDate: string | null,
  changedBy: string,
};

type HouseholdHistoryRowData = {
  id: string,
  residentId?: string,
  residentName?: string,
  moveInDate: string,
  moveOutDate: string | null,
  isTransfer?: boolean,
  household?: {
    id: string,
    mainCustomerId: string,
    mainCus?: {
      id: string,
      firstName: string,
      lastName: string,
      rc?: {
        id: string,
        customerId: string,
        residentId: string,
      },
    },
  },
};

type Props = {
  rows: UnitHistoryRowData[],
  intl?: Intl,
  onFloorPlanClick?: (floorPlanId?: string) => void,
  isLoading?: boolean,
  householdUnitHistoryProps?: {
    rows: HouseholdHistoryRowData[],
    onResidentClick?: (id: string, resident: string) => void,
    isLoading?: boolean,
    isError?: boolean,
  },
};

const tableGridSx = {
  maxHeight: 260,
  overflowY: 'auto',
  maxWidth: '100%',
  overflowX: 'auto',
};

const UnitHistoryTable = ({ rows = [], onFloorPlanClick, intl }: Props) => {
  const formatMessage = intl?.formatMessage;

  return (
    <Grid container role="table" aria-label="Unit History Table">
      <Grid
        container
        paddingX={2}
        role="row"
        aria-label="Unit History Table Header Row"
      >
        <Grid item xs={3} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.floorPlanColumn)}
          </Typography>
        </Grid>
        <Grid item xs={2} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.setAsideColumn)}
          </Typography>
        </Grid>
        <Grid item xs={2} role="cell">
          <Typography variant="h4">
            {formatMessage?.(messages.startDateColumn)}
          </Typography>
        </Grid>
        <Grid item xs={2} role="cell">
          <Typography variant="titleMedium">
            {formatMessage?.(messages.endDateColumn)}
          </Typography>
        </Grid>
        <Grid item xs={3} role="cell">
          <Typography variant="h4">
            {formatMessage?.(messages.changedByColumn)}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SizedBox h={5} />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid
        container
        role="rowgroup"
        aria-label="Unit History Table Rows"
        marginTop={1}
        paddingX={2}
        sx={tableGridSx}
      >
        {rows.map((rowData, i) => (
          <Grid container key={i} role="row">
            <Grid item xs={12}>
              <SizedBox h={5} />
            </Grid>
            <Grid container alignItems="center">
              <Grid item xs={3} role="cell">
                <Typography
                  variant="labelMedium"
                  fontSize={14}
                  role="button"
                  sx={{ color: 'primary.main', cursor: 'pointer' }}
                  onClick={() => {
                    onFloorPlanClick?.(rowData.floorPlanId);
                  }}
                >
                  {rowData.floorPlan}
                </Typography>
              </Grid>
              <Grid item xs={2} role="cell">
                <Typography>{rowData.setAside ?? '---'}</Typography>
              </Grid>
              <Grid item xs={2} role="cell">
                <Typography>
                  {rowData.startDate
                    ? formatDateDisplayUTC(rowData.startDate)
                    : '---'}
                </Typography>
              </Grid>
              <Grid item xs={2} role="cell">
                <Typography>
                  {rowData.endDate
                    ? formatDateDisplayUTC(rowData.endDate)
                    : '---'}
                </Typography>
              </Grid>
              <Grid item xs={3} role="cell">
                <Typography>{rowData.changedBy}</Typography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <SizedBox h={5} />
            </Grid>
            {i + 1 !== rows.length && (
              <Grid item xs={12}>
                <Divider />
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

const UnitHistory = (props: Props) => {
  const formatMessage = props.intl?.formatMessage;
  const flags = useFlags();
  const showFloorPlanAtMoveOut = Boolean(flags?.floorPlanChanges);

  const residentHistoryRows = useMemo(() => {
    return props.householdUnitHistoryProps?.rows || [];
  }, [props.householdUnitHistoryProps?.rows]);

  const content = useMemo(() => {
    if (props.isLoading) return <Spinner />;
    if (props.rows.length === 0)
      return (
        <Typography textAlign="center">
          {formatMessage?.(messages.emptyStateMessage)}
        </Typography>
      );
    return <UnitHistoryTable {...props} />;
  }, [props, formatMessage]);

  return (
    <Paper sx={{ paddingLeft: 0.5, paddingBottom: 2 }}>
      <Stack>
        <Typography variant="titleLarge" padding={2}>
          {formatMessage?.(messages.header)}
        </Typography>
        <Divider />
        <div style={{ height: 20 }} />
        {content}

        <div style={{ height: 20 }} />
        <Divider />
        <Typography variant="bodyLarge" padding={2}>
          {formatMessage?.(messages.residentHistory)}
        </Typography>
        {props.householdUnitHistoryProps?.isLoading ? (
          <Spinner />
        ) : residentHistoryRows.length === 0 ? (
          <Typography textAlign="center">
            {formatMessage?.(messages.emptyStateMessage)}
          </Typography>
        ) : (
          <ResidentHistoryTable
            rows={residentHistoryRows}
            onResidentClick={props.householdUnitHistoryProps?.onResidentClick}
            intl={props.intl}
            showFloorPlanAtMoveOut={showFloorPlanAtMoveOut}
          />
        )}
      </Stack>
    </Paper>
  );
};

UnitHistory.defaultProps = {
  isLoading: false,
  onFloorPlanClick: () => {},
};

export default UnitHistory;
